import { useState } from "react";
import { paverGlossOpt as gloss } from "../assets/opt";
import { paverUltraOpt as ultra } from "../assets/opt";
import { paverInvisibleOpt as invisible } from "../assets/opt";
import { paverMatteOpt as matte } from "../assets/opt";
import swatchTan from "../assets/polysweep/swatch-tan.jpg.asset.json";
import swatchPlatinum from "../assets/polysweep/swatch-platinum.jpg.asset.json";
import swatchBlack from "../assets/polysweep/swatch-black.jpg.asset.json";
import appliedTan from "../assets/polysweep/applied-tan.jpg.asset.json";
import appliedPlatinum from "../assets/polysweep/applied-platinum.jpg.asset.json";
import appliedBlack from "../assets/polysweep/applied-black.jpg.asset.json";
import { Reveal } from "./reveal";

const FINISHES = [
  {
    name: "Gloss Finish",
    label: "Hurricane Cat 5",
    desc: "Maximum shine and a true wet look — the boldest, most reflective finish we offer.",
    img: gloss.url,
  },
  {
    name: "Ultra Deep Enhancement",
    label: "Typhoon",
    desc: "Deepest color enhancement. Darkens and richens the pavers for a dramatic look.",
    img: ultra.url,
  },
  {
    name: "Invisible Sealer",
    label: "Break Wall",
    desc: "No shine, no color change. Guards against stains and water while pavers look untouched.",
    img: invisible.url,
  },
  {
    name: "Matte Natural Look",
    label: "Cyclone",
    desc: "No shine at all. Protects the surface while keeping the pavers looking dry and natural.",
    img: matte.url,
  },
];

const SANDS = [
  {
    name: "Tan",
    label: "Polysweep",
    desc: "Warm, natural sand tone. Blends in with tan, clay and earth-toned pavers.",
    img: appliedTan.url,
    swatch: swatchTan.url,
  },
  {
    name: "Platinum",
    label: "Polysweep",
    desc: "Neutral gray with light speckle. The safest match for most gray and multi-tone pavers.",
    img: appliedPlatinum.url,
    swatch: swatchPlatinum.url,
  },
  {
    name: "Black",
    label: "Polysweep",
    desc: "High-contrast charcoal joints that frame each paver and make the pattern pop.",
    img: appliedBlack.url,
    swatch: swatchBlack.url,
  },
];

const TABS = [
  {
    id: "finish" as const,
    label: "Sealer Finishes",
    blurb:
      "Pick the finish you want before we seal. Results vary by paver color and texture — these show the range from full gloss to a natural matte.",
    items: FINISHES,
  },
  {
    id: "sand" as const,
    label: "Joint Sand Colors",
    blurb:
      "After we clean, we re-sand the joints with polymeric sand to lock the pavers in and hold back weeds. Pick the color that fits your look.",
    items: SANDS,
  },
];

export function PaverLookOptions() {
  const [tab, setTab] = useState<"finish" | "sand">("finish");
  const active = TABS.find((t) => t.id === tab)!;

  return (
    <section className="bg-white py-14 md:py-16">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="text-center mb-6 md:mb-8">
          <p
            className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Customize Your Look
          </p>
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Finishes &amp; Sand Colors
          </h2>
        </div>

        <div
          role="tablist"
          aria-label="Paver finish and joint sand options"
          className="flex justify-center gap-2 mb-5"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 rounded-md text-sm uppercase tracking-wide transition-colors border ${
                tab === t.id
                  ? "bg-[var(--brand-deep-blue)] text-white border-[var(--brand-deep-blue)]"
                  : "bg-white text-[var(--brand-deep-blue)] border-gray-300 hover:border-[var(--brand-deep-blue)]"
              }`}
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <p className="text-center text-[var(--brand-charcoal)]/85 text-base max-w-2xl mx-auto leading-relaxed mb-8">
          {active.blurb}
        </p>

        <div
          className={`grid grid-cols-2 gap-4 md:gap-6 ${
            tab === "finish" ? "lg:grid-cols-4" : "lg:grid-cols-3"
          }`}
        >
          {active.items.map((item, i) => (
            <Reveal
              key={item.name}
              delayMs={i * 60}
              className="card-pop bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm"
            >
              <img
                src={item.img}
                alt={`${item.name} paver example`}
                width={900}
                height={900}
                loading="lazy"
                className="w-full aspect-square object-cover"
              />
              <div className="p-4 md:p-5">
                <div className="flex items-start gap-3">
                  {"swatch" in item && item.swatch ? (
                    <img
                      src={item.swatch}
                      alt={`${item.name} polymeric sand color swatch`}
                      width={550}
                      height={550}
                      loading="lazy"
                      className="w-12 h-12 rounded-md object-cover border border-gray-200 shrink-0"
                    />
                  ) : null}
                  <div>
                    <p
                      className="text-[var(--brand-red)] text-[11px] font-bold uppercase tracking-[0.18em] mb-0.5"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {item.label}
                    </p>
                    <h3
                      className="text-[var(--brand-deep-blue)] text-base md:text-lg uppercase mb-1.5 leading-tight"
                      style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                    >
                      {item.name}
                    </h3>
                    <p className="text-[var(--brand-charcoal)]/85 text-[13px] md:text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-5 text-center text-xs text-[var(--brand-charcoal)]/60 max-w-2xl mx-auto">
          Photos are for illustration only. Finish and sand color can vary with paver
          type, aggregates and moisture content.
        </p>
      </div>
    </section>
  );
}
